import { MapTrifoldIcon, TrashIcon, WarningIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { EmptyState } from '@/components/empty-state';
import { ICON_WEIGHT } from '@/components/icon';
import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { DEFAULT_SETTINGS, useSettings } from '@/features/settings';
import { tracks } from '@/lib/db';
import {
  formatCalendarDate,
  formatCount,
  formatDecimalHours,
  formatLocalDate,
  formatLocalTime,
  localZoneLabel,
} from '@/lib/format';
import {
  ALTITUDE_UNIT_LABEL,
  DISTANCE_UNIT_LABEL,
  convertAltitude,
  convertDistance,
  type UnitPreferences,
} from '@/lib/units';

import {
  type LogbookFilter,
  matchesLogbook,
  matchesSummaryQuery,
  matchesSummarySource,
  type SortId,
  sortSummaries,
  type SourceFilter,
} from './filters';
import { SOURCE_LABEL } from './labels';
import { airTimeMinutes, type FlightSummary, routeLabel, summaryLogbookStatus } from './summary';
import { useFlightSummaries } from './use-flight-summaries';
import { collapseWarnings } from './warnings';

/**
 * The flight list — every flight, not every file.
 *
 * It used to list `Track` rows and show only what the file itself said, because
 * nothing had derived a flight from one yet. Now that import writes a `Flight`
 * and drafts a logbook entry, a row can say what the flight *was*: where it
 * went, how long it took, how far, how high, how fast, and whether it has made
 * it into the logbook — with a link straight to that entry. A track nothing has
 * claimed still appears, because hiding an imported file from the list of
 * imported flights would be worse than showing it without numbers.
 *
 * Derived numbers stay visibly derived: they are the same `Flight.derived`
 * suggestions the flight page shows, and the logbook badge says out loud when
 * an entry is still a draft.
 */

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1.5">
      <dt>{label}</dt>
      <dd className="tabular text-foreground">{value}</dd>
    </div>
  );
}

function FlightRow({
  summary,
  units,
  dateOptions,
}: {
  summary: FlightSummary;
  units: UnitPreferences;
  dateOptions: { format?: 'nz' | 'iso'; timeZone?: string };
}) {
  const [confirming, setConfirming] = useState(false);
  const { track, entry, stats } = summary;

  // A track with no times took the import clock for both ends, so times read
  // off them would be a fact invented by the importer.
  const timeless = track?.warnings.some((warning) => warning.kind === 'no-timestamps') ?? false;
  const airMin = airTimeMinutes(summary);
  const status = summaryLogbookStatus(summary);
  const route = routeLabel(summary);
  const title = summary.registration ?? track?.callsign ?? 'Unknown aircraft';

  /*
   * The whole card opens the flight. It used to carry View map, Review entry
   * and Remove as three buttons on every row, plus seven labelled numbers —
   * forty flights made a wall of identical buttons. The title link is
   * stretched over the card (`after:absolute after:inset-0`), and the few
   * controls that do something else are positioned so they paint above it.
   * Fixes, source and file name are on the flight page, where they are
   * looked for; the list keeps what tells two flights apart.
   */
  return (
    <li className="border-border bg-surface rounded-container hover:border-border-strong relative border px-4 py-3 transition-colors duration-150">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {track === undefined ? (
          <span className="text-sm font-semibold tracking-tight">{title}</span>
        ) : (
          <Link
            to="/flights/$trackId"
            params={{ trackId: track.id }}
            className="text-sm font-semibold tracking-tight after:absolute after:inset-0 after:content-['']"
          >
            {title}
          </Link>
        )}
        {route === undefined ? null : <span className="text-foreground text-sm">{route}</span>}
        <p className="text-muted-foreground text-sm">
          {timeless ? (
            'Time unknown'
          ) : summary.startTime === undefined ? (
            formatCalendarDate(summary.date, dateOptions)
          ) : (
            <>
              {formatLocalDate(summary.startTime, dateOptions)}
              {' · '}
              <span className="tabular">{formatLocalTime(summary.startTime, dateOptions)}</span>
              {summary.endTime === undefined ? null : (
                <>
                  {'–'}
                  <span className="tabular">{formatLocalTime(summary.endTime, dateOptions)}</span>
                </>
              )}{' '}
              {localZoneLabel(summary.startTime, dateOptions)}
            </>
          )}
        </p>
        <span className="ml-auto">
          <StatusBadge status={status} data-testid="logbook-status" />
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <dl className="text-muted-foreground flex flex-wrap gap-x-5 gap-y-1 text-xs">
          {/*
            A flight nothing has derived yet still knows how long the file
            covers, and that was what the list showed before flights existed —
            so it keeps showing it, under the name it deserves. Wall-clock
            coverage is not air time and is never labelled as if it were.
          */}
          {airMin === undefined && track !== undefined && !timeless ? (
            <Fact
              label="Duration"
              value={`${formatDecimalHours(
                (Date.parse(track.endTime) - Date.parse(track.startTime)) / 3_600_000,
              )} hr`}
            />
          ) : (
            <Fact
              label="Flight time"
              value={airMin === undefined ? '—' : `${formatDecimalHours(airMin / 60)} hr`}
            />
          )}
          <Fact
            label="Distance"
            value={
              stats.distanceNm === undefined
                ? '—'
                : `${formatCount(Math.round(convertDistance(stats.distanceNm, units.distance)))} ${DISTANCE_UNIT_LABEL[units.distance]}`
            }
          />
          <Fact
            label="Max alt"
            value={
              stats.maxAltFt === undefined
                ? '—'
                : `${formatCount(Math.round(convertAltitude(stats.maxAltFt, units.altitude)))} ${ALTITUDE_UNIT_LABEL[units.altitude]}`
            }
          />
          {/*
            A track nothing has claimed has no route or air time to tell it
            apart, so it shows what the file itself says instead.
          */}
          {summary.flight !== undefined || track === undefined ? null : (
            <>
              <Fact label="Fixes" value={formatCount(track.pointCount)} />
              <div className="flex gap-1.5">
                <dt>Source</dt>
                <dd className="text-foreground">{SOURCE_LABEL[track.source]}</dd>
              </div>
              {track.sourceRef === undefined ? null : (
                <div className="flex min-w-0 gap-1.5">
                  <dt>File</dt>
                  <dd className="text-foreground truncate" title={track.sourceRef}>
                    {track.sourceRef}
                  </dd>
                </div>
              )}
            </>
          )}
          {summary.aircraft === undefined ? null : (
            <div className="relative flex min-w-0 gap-1.5">
              <dt>Aircraft</dt>
              <dd className="text-foreground truncate">
                <Link
                  to="/aircraft/$aircraftId"
                  params={{ aircraftId: summary.aircraft.id }}
                  className="hover:text-accent underline-offset-2 hover:underline"
                >
                  {[summary.aircraft.make, summary.aircraft.model].filter(Boolean).join(' ') ||
                    summary.aircraft.registration}
                </Link>
              </dd>
            </div>
          )}
        </dl>

        <div className="relative flex items-center gap-1">
          {confirming && track !== undefined ? (
            <>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  void tracks.delete(track.id);
                }}
              >
                Remove permanently
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Keep
              </Button>
            </>
          ) : (
            <>
              {entry === undefined ? null : (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/logbook" hash={entry.id}>
                    {entry.isDraft ? 'Review entry' : 'Logbook entry'}
                  </Link>
                </Button>
              )}
              {summary.mergedIntoEntryId === undefined ? null : (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/logbook" hash={summary.mergedIntoEntryId}>
                    Merged entry
                  </Link>
                </Button>
              )}
              {track === undefined ? null : (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove the ${SOURCE_LABEL[track.source]} track from ${
                    track.sourceRef ?? 'an unnamed file'
                  }`}
                  title="Remove"
                  onClick={() => {
                    setConfirming(true);
                  }}
                >
                  <TrashIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {track === undefined || track.warnings.length === 0 ? null : (
        <ul className="mt-2 space-y-1">
          {collapseWarnings(track.warnings).map((warning) => (
            <li
              // Collapsed to one line per kind, so the kind is a stable key.
              key={warning.kind}
              className="text-muted-foreground flex gap-1.5 text-xs leading-relaxed"
            >
              <WarningIcon
                aria-hidden="true"
                weight={ICON_WEIGHT}
                className="mt-0.5 size-3.5 shrink-0"
              />
              <span>{warning.message}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * How many flights search and sort look across, and how many rows render.
 * Both fixed rather than paging through IndexedDB — the same trade-off the
 * track list made before it, just applied to the joined view.
 */
const FETCH_LIMIT = 500;
const RENDER_LIMIT = 50;

export interface FlightListProps {
  query?: string;
  source?: SourceFilter;
  sort?: SortId;
  logbook?: LogbookFilter;
}

export function FlightList({
  query = '',
  source = 'all',
  sort = 'recent',
  logbook = 'all',
}: FlightListProps) {
  const fetched = useFlightSummaries(FETCH_LIMIT);
  const settings = useSettings() ?? DEFAULT_SETTINGS;
  const units: UnitPreferences = {
    altitude: settings.altitudeUnit,
    speed: settings.speedUnit,
    distance: settings.distanceUnit,
  };
  const dateOptions = { format: settings.dateFormat, timeZone: settings.timeZone };

  if (fetched === null) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="track-list-loading">
        Opening the local database…
      </p>
    );
  }

  if (fetched.length === 0) {
    return null;
  }

  const matching = fetched.filter(
    (summary) =>
      matchesSummaryQuery(summary, query) &&
      matchesSummarySource(summary, source) &&
      matchesLogbook(summary, logbook),
  );
  const sorted = sortSummaries(matching, sort);
  const rows = sorted.slice(0, RENDER_LIMIT);

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={MapTrifoldIcon}
        title="No flights match"
        description="Clear the search or filter to see more flights."
      />
    );
  }

  return (
    <>
      <ul className="space-y-3" data-testid="track-list">
        {rows.map((summary) => (
          <FlightRow key={summary.id} summary={summary} units={units} dateOptions={dateOptions} />
        ))}
      </ul>
      {sorted.length <= RENDER_LIMIT ? null : (
        <p className="text-muted-foreground text-sm" data-testid="track-list-truncated">
          Showing {formatCount(RENDER_LIMIT)} of {formatCount(sorted.length)} matching flights.
        </p>
      )}
      {fetched.length < FETCH_LIMIT ? null : (
        <p className="text-muted-foreground text-xs" data-testid="track-list-fetch-cap">
          Search and sort cover the {formatCount(FETCH_LIMIT)} most recent flights only. More are
          stored in this browser.
        </p>
      )}
    </>
  );
}
